import React from 'react'; // eslint-disable-line no-unused-vars
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';

import Api from '../apis/apiAssets';
import { g_Auth } from '../apis/apiAuth';
import { onSettledT, onMutateCreateT, onMutateDeleteT, onMutateUpdateT, mutationFnUpdateT, Q_DEF_RETRY, Q_STALE_TMO_CFG } from '../utils/utils';
import { getDateRangePast30Days, getDateRangePast60Days, getDateRangePast7Days } from '../utils/dateTimeUtils';

// /////////////////////////////////////////////////////////////////////////////////////////////////
// Asset API - Cached by g_Auth.getOrgId()
// /////////////////////////////////////////////////////////////////////////////////////////////////
const entityKeys = (props = {}) => {
  const { pagination = { pageIndex: 0, pageSize: 0 }, filters, sorting } = props;
  const filtersKey = filters ? JSON.stringify(filters) : '';
  const sortingKey = sorting ? JSON.stringify(sorting) : '';
  const keys = [
    'Asset-Device',
    g_Auth.getOrgId(),
    pagination?.pageIndex, // refetch when pagination.pageIndex changes
    pagination?.pageSize, // refetch when pagination.pageSize changes
    filtersKey,
    sortingKey,
  ];
  return keys;
};
const idName = 'entityId';

// /////////////////////////////////////////////////////////////////////////////////////////////////
export function useGetAssets(props = {}) {
  const { pagination, refetchInterval = 0, notifyOnChangeProps, extraFields, filters, sorting } = props;
  const reqProps = {
    limit: pagination.pageSize,
    offset: pagination.pageSize * pagination.pageIndex,
    extraFields: 'healthInfo,telemetryInfo,locationInfo',
    ...(filters && { filters: JSON.stringify(filters) }),
    ...(sorting && { sorting: JSON.stringify(sorting) }),
  };

  if (extraFields) {
    reqProps.extraFields += `,${extraFields}`;
  }

  return useQuery({
    queryKey: entityKeys(props),
    queryFn: async () => {
      const res = await Api.apiAssetsGet(reqProps).catch((err) => Promise.reject(err));
      if (res.status !== 200) {
        console.warn('useGetAssets Fail', res);
        return Promise.reject(res);
      }
      return res.dataJson;
    },
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData, // don't go to 0 rows when refetching or paginating to next page
    refetchInterval,
    notifyOnChangeProps,
    retry: Q_DEF_RETRY,
    staleTime: Q_STALE_TMO_CFG,
  });
}

// /////////////////////////////////////////////////////////////////////////////////////////////////
export function useGetDeletedAssets(props = {}) {
  const { pagination, refetchInterval = 0 } = props;
  const reqProps = {
    limit: pagination.pageSize,
    offset: pagination.pageSize * pagination.pageIndex,
    extraFields: '',
  };

  return useQuery({
    queryKey: entityKeys(props),
    queryFn: async () => {
      const res = await Api.apiAssetsGetDeleted(reqProps).catch((err) => Promise.reject(err));
      if (res.status !== 200) {
        console.warn('useGetDeletedAssets Fail', res);
        return Promise.reject(res);
      }
      return res.dataJson;
    },
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData, // don't go to 0 rows when refetching or paginating to next page
    refetchInterval,
    retry: Q_DEF_RETRY,
    staleTime: Q_STALE_TMO_CFG,
  });
}

// /////////////////////////////////////////////////////////////////////////////////////////////////
export function useCreateAsset(props = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (values) => {
      //send api update request here
      console.log('useCreateAsset', 'mutationFn values:', values);
      const res = await Api.apiAssetCreate(values);
      return Promise.resolve(res?.dataJson);
    },
    onMutate: (newInfo) => onMutateCreateT(queryClient, entityKeys(props), newInfo), //client side optimistic update
    onSettled: () => onSettledT(queryClient, entityKeys(props)),
  });
}

// /////////////////////////////////////////////////////////////////////////////////////////////////
export function useUpdateAsset(props = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ values, origValues }) =>
      mutationFnUpdateT({ entityType: origValues.entityType, values, origValues, updateCB: Api.apiAssetUpdate }),
    onMutate: (newInfo) => onMutateUpdateT(queryClient, entityKeys(props), newInfo, idName), //client side optimistic update
    onSettled: () => onSettledT(queryClient, entityKeys(props)),
  });
}

// /////////////////////////////////////////////////////////////////////////////////////////////////
export function useDeleteFleetAsset(props = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => await Api.apiAssetUpdate({ entityId: id, fleetId: '' }),
    onMutate: (id) => onMutateDeleteT(queryClient, entityKeys(props), id, idName), //client side optimistic update
    onSettled: () => onSettledT(queryClient, entityKeys(props)),
  });
}

// /////////////////////////////////////////////////////////////////////////////////////////////////
export function useDeleteAsset(props = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => await Api.apiAssetDelete(id),
    onMutate: (id) => onMutateDeleteT(queryClient, entityKeys(props), id, idName), //client side optimistic update
    onSettled: () => onSettledT(queryClient, entityKeys(props)),
  });
}

// /////////////////////////////////////////////////////////////////////////////////////////////////
// /////////////////////////////////////////////////////////////////////////////////////////////////
// /////////////////////////////////////////////////////////////////////////////////////////////////

const miscEntityKeys = (props = {}) => {
  const { entityType, pagination = { pageIndex: 0, pageSize: 0 }, fleetId = '', tags, filter, dateRange } = props;
  const keys = [
    entityType,
    fleetId,
    g_Auth.getOrgId(),
    pagination?.pageIndex, // refetch when pagination.pageIndex changes
    pagination?.pageSize, // refetch when pagination.pageSize changes
    { tags: Array.isArray(tags) ? tags : [] },
    filter,
    ...(dateRange ? JSON.stringify(dateRange) : ''),
  ];
  return keys;
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
export function useGetVehiclesData(props = {}) {
  const { filter, timezoneIana } = props;
  let dateRange;
  if (filter === 7) {
    dateRange = getDateRangePast7Days(timezoneIana);
  } else if (filter === 30) {
    dateRange = getDateRangePast30Days(timezoneIana);
  } else if (filter === 60) {
    dateRange = getDateRangePast60Days(timezoneIana);
  } else {
    dateRange = getDateRangePast7Days(timezoneIana);
    console.warn('useGetVehiclesData: invalid filter', filter, 'using default 7 days');
  }

  const reqProps = {
    startTime: dateRange?.from,
    endTime: dateRange?.to,
  };

  return useQuery({
    queryKey: miscEntityKeys({ entityType: 'AssetsFilter', filter }),
    queryFn: async () => {
      const res = await Api.apiAssetsFilterGet(reqProps).catch((err) => Promise.reject(err));
      if (res.status !== 200) {
        console.warn(res);
        return Promise.reject(res);
      }
      return res.dataJson;
    },
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData, // don't go to 0 rows when refetching or paginating to next page
    retry: Q_DEF_RETRY,
    staleTime: Q_STALE_TMO_CFG,
  });
}

export function useGetInstalledAssets(props = {}) {
  const { pagination, refetchInterval = 0, dateRange = {}, tags = [], fleetId } = props;
  const reqProps = {
    limit: pagination.pageSize,
    offset: pagination.pageSize * pagination.pageIndex,
    startTime: dateRange?.from,
    endTime: dateRange?.to,
    ...(tags && Array.isArray(tags) && tags.length > 0 && { tags }),
    fleetId,
  };

  return useQuery({
    queryKey: miscEntityKeys({ entityType: 'InstalledAssets', pagination, dateRange, tags, fleetId }),
    queryFn: async () => {
      const res = await Api.apiInstalledAssetsGet(reqProps).catch((err) => Promise.reject(err));
      if (res.status !== 200) {
        console.warn('useGetAssets Fail', res);
        return Promise.reject(res);
      }
      return res.dataJson;
    },
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData, // don't go to 0 rows when refetching or paginating to next page
    refetchInterval,
    retry: Q_DEF_RETRY,
    staleTime: Q_STALE_TMO_CFG,
  });
}
